import React, { createContext, useContext, useState, useEffect } from 'react';
import {Typography} from "@mui/material";
//import {ColumnConfig} from "../components/userTypes";
import {GridColumnConfig} from "../types";
import axiosApexInstance from "../auth/axiosApexInstance";
import { useUser } from './UserContext';
import {MISSING_TOKEN_ERROR_MSG} from "../Constants";

export interface GridConfigDTO {
    id: number;
    configName: string;
    configJson: string;
    delflag: number;
    createdAt: string;
    modifiedAt: string;
}

interface GridConfigContextProps {
    configs: GridConfigDTO[];
    loadConfigs: () => void;
    saveConfig: (config: GridConfigDTO) => Promise<void>;
    deleteConfig: (id: number) => Promise<void>;
    getConfigByName: <T = any>(name: string) => Partial<Record<keyof T, GridColumnConfig<T>>> | undefined;
    rehydrateConfig: <T = any>(config: any) => Partial<Record<keyof T, GridColumnConfig<T>>>;
}

const GridConfigContext = createContext<GridConfigContextProps | undefined>(undefined);

export const GridConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [configs, setConfigs] = useState<GridConfigDTO[]>([]);
    const{accessToken} = useUser();
    // const defaultRcColumnConfig = {
    //     octxId: {
    //         header: "ID",
    //         defaultShow: true,
    //         displayOrder: 1,
    //         width: 80
    //     },
    //     octxDate: {
    //         header: "Date",
    //         defaultShow: true,
    //         displayOrder: 2,
    //         width: 180
    //     },
    //     octxSubcategory: {
    //         header: "Type",
    //         defaultShow: true,
    //         displayOrder: 3,
    //         width: 100
    //     },
    //     workflowStatus: {
    //         header: "Status",
    //         type: "singleSelect",
    //         valueOptions: ["NEW", "INPROGRESS", "DONE", "ONHOLD"],
    //         editable: true,
    //         defaultShow: true,
    //         displayOrder: 2,
    //         rendererKey: "status" // replaced renderValue
    //     },
    //     fromName: {
    //         header: "From",
    //         defaultShow: true,
    //         displayOrder: 5
    //     },
    //     octxFilepath: {
    //         header: "File Type",
    //         defaultShow: true,
    //         displayOrder: 6,
    //         width: 120,
    //         valueGetterKey: "fileExt",   // replaced valueGetter
    //         rendererKey: "fileExt"       // replaced renderValue
    //     },
    //     fromNumber: {
    //         header: "From #",
    //         defaultShow: true,
    //         displayOrder: 7
    //     },
    //     toName: {
    //         header: "To",
    //         defaultShow: true,
    //         displayOrder: 8
    //     },
    //     toNumber: {
    //         header: "To #",
    //         defaultShow: true,
    //         displayOrder: 9
    //     },
    //     octxNotes: {
    //         header: "Notes",
    //         defaultShow: true,
    //         displayOrder: 10
    //     }
    // };


    const loadConfigs = async () => {
        try {
            const res = await axiosApexInstance.get('/task/gridconfig/all');
            // console.log("Retrieved grid config rows", res.data); // Removed verbose logging
            setConfigs(res.data);
        }catch (error: any) {
            // Suppress error log if it's due to missing access token (pre-login case)
            if (error?.message === MISSING_TOKEN_ERROR_MSG ) {
                return;
            }

            console.error('Failed to fetch GridConfig', error);
        }
    };

    const saveConfig = async (config: GridConfigDTO) => {
        await axiosApexInstance.post('/task/gridconfig/save', config);
        await loadConfigs();
    };

    const deleteConfig = async (id: number) => {
        await axiosApexInstance.delete(`/task/gridconfig/${id}`);
        await loadConfigs();
    };

    useEffect(() => {
        loadConfigs();
    }, [accessToken]);

    // const getConfigByName = (name: string) => {
    //     // const found = configs.find(cfg => cfg.configName === name);
    //     // try {
    //     //     return found ? JSON.parse(found.configJson) : undefined;
    //     // } catch {
    //     //     return undefined;
    //     // }
    //     //
    //
    //     return defaultRcColumnConfig as Partial<Record<keyof T, ColumnConfig<T>>>;
    //     from this method return   Partial<Record<keyof T, ColumnConfig<T>>>;
    // };

    const renderers = {
        status: (val: any) => <Typography>{val || '—'}</Typography>,
        fileExt: (val: string) => <Typography variant="body2">{val}</Typography>
    };

    const valueGetters = {
        fileExt: (row: any) => {
            const val = row.octxFilepath || '';
            return val.split('.').pop()?.toUpperCase() || '';
        }
    };

    const rehydrateConfig = <T = any>(config: any): Partial<Record<keyof T, GridColumnConfig<T>>> => {
        if (!config || typeof config !== 'object') return {};



        const result: Partial<Record<keyof T, GridColumnConfig<T>>> = {};

        for (const key in config) {
            const col = config[key];

            if (col && typeof col === 'object' && !Array.isArray(col)) {
                const hydratedCol = { ...col } as GridColumnConfig<T>;

                // console.log("Hydrating", key, hydratedCol); // Removed verbose logging

                if ('rendererKey' in hydratedCol && typeof hydratedCol.rendererKey === 'string') {
                    hydratedCol.renderValue = renderers[hydratedCol.rendererKey as keyof typeof renderers];
                }

                if ('valueGetterKey' in hydratedCol && typeof hydratedCol.valueGetterKey === 'string') {
                    hydratedCol.valueGetter = valueGetters[hydratedCol.valueGetterKey as keyof typeof valueGetters];
                }

                result[key as keyof T] = hydratedCol;
            }
        }

        return result;
    };


    const getConfigByName = <T = any>(name: string): Partial<Record<keyof T, GridColumnConfig<T>>> => {
        const found = configs.find(cfg => cfg.configName === name);
        if (!found) return {};

        try {
            // eslint-disable-next-line no-new-func
            const parsed = new Function(`return (${found.configJson})`)(); // ✅ parses JS object string
            const hydrated = rehydrateConfig(parsed);
            // console.log("Hydrated config", hydrated); // Removed verbose logging
            return hydrated;
        } catch (err) {
            console.warn(`Invalid config: ${name}`, err);
            return {};
        }
    };




    return (
        <GridConfigContext.Provider value={{ configs, loadConfigs, saveConfig, deleteConfig , getConfigByName, rehydrateConfig  }}>
            {children}
        </GridConfigContext.Provider>
    );
};

export const useGridConfig = () => {
    const context = useContext(GridConfigContext);
    if (!context) throw new Error("useGridConfig must be used within a GridConfigProvider");
    return context;
};
